import { toStringHDMS } from 'ol/coordinate';
import { Projection, fromLonLat, get as getProjection, toLonLat, transform, transformExtent } from 'ol/proj';
import { unByKey } from 'ol/Observable';
import { defaults } from 'ol/control/defaults';
import DoubleClickZoom from 'ol/interaction/DoubleClickZoom';
import DragPan from 'ol/interaction/DragPan';
import DragRotateAndZoom from 'ol/interaction/DragRotateAndZoom';
import { Draw, Snap } from 'ol/interaction';
import GeoJSON from 'ol/format/GeoJSON.js';
import FullScreen from 'ol/control/FullScreen';
import Map from 'ol/Map';
import MousePosition from 'ol/control/MousePosition';
import Overlay from 'ol/Overlay';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import View from 'ol/View';
import Control from 'ol/control/Control';
import { LineString, Point, Polygon } from 'ol/geom';
import Feature from 'ol/Feature';
import LayerGroup from 'ol/layer/Group';
import { Cluster } from 'ol/source';
import { createEmpty, extend, getCenter, getWidth, getHeight } from 'ol/extent';
import OverviewMap from 'ol/control/OverviewMap.js';
import { bbox as bboxStrategy } from 'ol/loadingstrategy';
import * as olExtent from 'ol/extent';
import Circle from 'ol/geom/Circle.js';
import { circular } from 'ol/geom/Polygon.js';

import { formatLength, formatAzimuth, parseDMS } from './utils.js';
import { dictLayerNames, getAvailablelayerByKey, getFeauresFromJson } from './layers.js';
import { addToolTip, createSpinner, getPositionPixels, getListOrder } from './htmlbuilder.js';
import {
  styleDistance,
  styleDraw,
  transparentFill,
  transparentStroke,
  styleStar,
  styleShadowDistance,
  stylePolygon,
  cloneStyle,
  getolStyle,
  getStyleForJsonLayer,
  pointCoordsStyle,
  styleCluster,
  styleCluster2,
  getStyleFromString,
} from './olStyles.js';
import { addInteractionHover, addInteractionPopup, addInteractionLineTooltip } from './interactions-popup.js';
import { iconsSVG } from './icons-svg.js';

const projectionForIdwmGetInfo = getProjection('EPSG:4326');
const layerstoIgnore = ['graticule', 'distance', 'highlightCtry', 'getCoordinates', 'getContour'];
const lineTooltipInteractionKey = 'lineTooltipInteraction';
const lineTooltipOverlayKey = 'lineTooltipOverlay';

export function createMap(container, view) {
  let layerDistance;
  let measureTooltipElement, measureTooltipOverlay;

  // Define Controls
  let defaultControls = defaults({
    attributionOptions: {
      collapsible: false,
    },
  });

  let mousePositionControl = createMousePositionControl(container);

  let extendedControls = [mousePositionControl, new FullScreen()];

  // Combine default and extended controls
  let mapControls = defaultControls.extend(extendedControls);

  // Define View
  let mapView = new View({
    //projection: 'EPSG:4326',
    center: fromLonLat(view?.center ?? [0, 0]),
    zoom: view?.zoom ?? 2,
    minZoom: 0,
    rotation: 0,
  });

  // console.log('before IsGeoServerAvailable2');

  // Create Map
  let map = new Map({
    target: container.id,
    layers: [], //olLayers,
    view: mapView,
    controls: mapControls,
  });

  // Define Interactions
  let interactions = [new DragRotateAndZoom(), new DoubleClickZoom(), new DragPan()];

  // Add Interactions to the Map
  interactions.forEach((interaction) => map.addInteraction(interaction));

  //Add clear layers button
  let dist = createDistanceTool({
    map,
    layerDistance,
    measureTooltipElement,
    measureTooltipOverlay,
  });
  dist.map = map;
  let rulerControl = new RulerControl(dist);

  map.addControl(rulerControl);

  map.markerIndex = 1;

  window[container.id] = {
    positionPicker: null,
  };
  return map;
}

class RulerControl extends Control {
  constructor(dist) {
    let { map, draw, layerDistance, measureTooltipElement, measureTooltipOverlay } = dist;
    //let options || {};
    let mapContainer = map?.getTarget();
    let idRuler = 'oim-ruler-' + mapContainer;
    let button = document.createElement('button');
    button.className = 'clear-layers btn btn-light float-end mt-1 p-1';
    button.innerHTML = `<i style="font-size:1.2rem;" id="${idRuler}">${iconsSVG.ruler2}</i>`;
    addToolTip(button, 'Distance/Azimuth', 'left');
    // Add a click event listener to the button
    button.addEventListener('click', function () {
      let newStatus = !draw.getActive();
      draw.setActive(newStatus);
      let ruler = document.getElementById(idRuler);
      let css = 'oim-icon-active';
      if (newStatus) {
        button.classList.add(css);
        //ruler.style.backgroundColor=""
      } else {
        button.classList.remove(css);
        if (layerDistance) {
          map.removeLayer(layerDistance);
        }
        measureTooltipOverlay?.setPosition(null);
        //measureTooltipElement?.remove();
        const elementToRemove = document.getElementById('ol-tooltip-measure-' + map.getTarget());
        elementToRemove?.remove();
      }
    });

    super({
      element: button,
      //target: options.target,
    });
  }
}

function createMousePositionControl(parentContainer) {
  let containerClass = 'oim-coordinates-container';
  const containerCoordinates = document.createElement('div');
  //const containerCoordinates = parentContainer.createElement('div');

  containerCoordinates.id = containerClass + '-' + parentContainer.id;
  containerCoordinates.className = containerClass;
  containerCoordinates.style.position = 'absolute';
  let right = getPositionPixels(parentContainer, 15, 'right');
  containerCoordinates.style.right = right;
  parentContainer.appendChild(containerCoordinates);

  return new MousePosition({
    coordinateFormat: function (coord) {
      let result;
      if (coord.length >= 2) {
        const re = /N|S/;
        result = toStringHDMS(coord)?.replaceAll(' ', '');
        let isN = result?.includes('N');
        result = result?.split(re);
        if (result?.length >= 2) {
          result = result.reverse();
          result[1] = isN ? result[1] + 'N' : result[1] + 'S';
          result = result.join(' ');
        }
      }
      return result;
    },
    //coordinateFormat: createStringXY(4),
    projection: 'EPSG:4326',
    className: 'oim-coordinates-text',
    target: document.getElementById(containerCoordinates.id),
    placeholder: '',
  });
}

export function toggleLayerVisibilityByName(layerName, map) {
  const layer = getLayerByName(layerName, map);
  if (layer !== undefined) {
    layer.setVisible(!layer.getVisible());
  }
}

export function setLayerOpacityyName(layerName, map, opacity) {
  const layer = getLayerByName(layerName, map);
  if (layer !== undefined) {
    layer.setOpacity(opacity);
  }
}
export function getLayerOpacityByName(layerName, map) {
  const layer = getLayerByName(layerName, map);
  let opacity = 1;
  if (layer !== undefined) {
    opacity = layer.getOpacity();
  }
  return opacity;
}

export function getLayerByName(layerName, map) {
  return map
    .getLayers()
    .getArray()
    .find((layer) => layer.get('name') === layerName);
}

function setZIndex(map, layerName, index) {
  //map.getLayers().getArray().forEach(function (layer) {
  //    if (layer.get('name') === layerName) {
  //        layer.setZIndex(index);
  //        //map.getLayers().setAt(index, layer);
  //    }
  //});

  let myLayers = map.getLayers();
  myLayers.forEach((layer) => {
    let name = layer.get('name')?.trim();
    if (name == layerName) {
      layer.setZIndex(index);
      return;
    }
  });
}

export function setZIndexs(map, layersOrder) {
  let minZIndex = 500;
  let layerOrderReversed = layersOrder.reverse();

  layerOrderReversed.forEach((layerName) => {
    const layer = getLayerByName(layerName, map);
    minZIndex = setLayerZIndex(layer, minZIndex);
  });
}

function setLayerZIndex(layer, minZIndex) {
  if (layer !== undefined) {
    if (layer instanceof LayerGroup) {
      let subLayers = layer.getLayers().getArray();
      subLayers.sort((a, b) => {
        return a.getZIndex() - b.getZIndex();
      });
      subLayers.forEach((subLayer) => {
        minZIndex++;
        subLayer.setZIndex(minZIndex);
      });
    }
    minZIndex++;
    layer.setZIndex(minZIndex);
  }
  return minZIndex;
}

export function setZIndexsOnLayers(layers, layersOrder) {
  let minZIndex = 500;
  let layerOrderReversed = layersOrder.reverse();
  layerOrderReversed.forEach((layerName) => {
    const layer = layers.find((layer) => layer.get('name') == layerName);
    minZIndex = setLayerZIndex(layer, minZIndex);
  });
}

function createDistanceTool(params) {
  let { map, layerDistance, measureTooltipElement, measureTooltipOverlay } = params;
  // Create a source to store the drawn points
  var source = new VectorSource();

  // Add a layer to display the drawn points
  layerDistance = new VectorLayer({
    name: 'distance',
    zIndex: 9999,
    source: source,
    style: styleDistance,
  });

  // Add the layer to the map
  map.addLayer(layerDistance);

  // Create a Draw interaction for drawing Point features
  var draw = new Draw({
    source: source,
    type: 'LineString',
    maxPoints: 2,
    style: styleDraw,
  });

  // Add the Draw interaction to the map
  map.addInteraction(draw);
  draw.setActive(false);

  // Variable to store the coordinates of the two points [longitude, latitude]
  var line = { start: [], end: [] };
  var listener;
  var sketch;
  const projectionCode = map.getView()?.getProjection()?.getCode();
  const epsg4326 = 'EPSG:4326';

  ({ measureTooltipElement, measureTooltipOverlay } = createMeasureTooltip(map));

  draw.on('drawstart', function (evt) {
    map.removeLayer(layerDistance);
    sketch = null;
    source.clear();
    if (measureTooltipOverlay) {
      measureTooltipOverlay.setPosition(null);
    }

    ({ measureTooltipElement, measureTooltipOverlay } = createMeasureTooltip(map));

    sketch = evt.feature;

    var tooltipCoord = evt.coordinate;

    listener = sketch.getGeometry().on('change', function (evt) {
      var geom = evt.target;
      var output = formatLength(geom) + ' / ' + formatAzimuth(line);
      tooltipCoord = geom.getLastCoordinate();

      measureTooltipElement.innerHTML = output + '<span> &#176;</span>';
      if (measureTooltipOverlay) {
        measureTooltipOverlay.setPosition(tooltipCoord);
        let [longitude, latitude] =
          projectionCode === epsg4326 ? tooltipCoord : transform(tooltipCoord, projectionCode, epsg4326);
        if (line.start.length === 0) {
          line.start = [longitude, latitude];
        }
        line.end = [longitude, latitude];
      }
    });
  });

  draw.on('drawend', function () {
    map.addLayer(layerDistance);
    line = { start: [], end: [] };
    unByKey(listener);
  });

  function createMeasureTooltip(map) {
    let measureTooltipElement = document.createElement('div');
    measureTooltipElement.className = 'ol-tooltip ol-tooltip-measure';
    measureTooltipElement.id = 'ol-tooltip-measure-' + map.getTarget();
    measureTooltipOverlay = new Overlay({
      element: measureTooltipElement,
      offset: [0, -15],
      positioning: 'bottom-center',
    });
    map.addOverlay(measureTooltipOverlay);
    return { measureTooltipElement, measureTooltipOverlay };
  }

  return {
    draw,
    layerDistance,
    measureTooltipElement,
    measureTooltipOverlay,
  };
}

export function addLayerByName(map, layerName, addedFlag, isGeoServerAvailable, isProd) {
  let spinner = createSpinner();

  let mapContainer = document.getElementById(map.getTarget());
  mapContainer.appendChild(spinner);
  //document.body.appendChild(spinner);
  let key = Object.keys(dictLayerNames).find((key) => dictLayerNames[key].label === layerName);
  let layer = getAvailablelayerByKey(isGeoServerAvailable, isProd, key);
  if (layer) {
    layer.setVisible(true);
    layer.setZIndex(getZIndexNext(map));
    if (!layer.get('labels')) {
      hideLabels(layer);
    }
    map.addLayer(layer);
    map.once('rendercomplete', function (event) {
      spinner.remove();
      addedFlag.innerHTML = 'Loaded';
      addedFlag.style.display = 'inline';
      //disableElement(btn);
    });
    //addInteraction(map, layer);
  }
  return layer;
}

export function hideLabels(layer) {
  if (!(layer instanceof VectorLayer) && !(layer instanceof LayerGroup)) {
    return;
  }
  let style;
  if (layer instanceof VectorLayer) {
    style = getStyle(layer);
  } else {
    // is a LayerGroup
    const subLayers = layer.getLayers().getArray();
    layer = subLayers.find((subLayer) => isPointsLayer(subLayer));
    if (layer) {
      style = getStyle(layer);
    }
  }

  if (style) {
    if (style.getText) {
      hideStyle(style);
    } else if (Array.isArray(style)) {
      let s = style.find((x) => x.getText());
      if (s) {
        hideStyle(s);
      }
    }
    layer.setStyle(style);
  }
  function getStyle(layer) {
    let style = layer?.getStyle();
    if (typeof style === 'function') {
      style = style(); // Ensure style is not a function
    }
    return style;
  }
  function hideStyle(style) {
    let textStyle = style.getText();
    if (textStyle) {
      textStyle.setFill(transparentFill);
      textStyle.setStroke(transparentStroke);
    }
  }
}

export function showLabels(layer) {
  let propName = 'styleOrigin';
  if (!(layer instanceof VectorLayer) && !(layer instanceof LayerGroup)) {
    return;
  }
  if (layer instanceof VectorLayer) {
    layer.setStyle(layer.get(propName));
  } else {
    // is a LayerGroup
    const subLayers = layer.getLayers().getArray();
    const pointsLayer = subLayers.find((subLayer) => isPointsLayer(subLayer));
    if (pointsLayer) {
      pointsLayer.setStyle(pointsLayer.get(propName));
    }
  }
}

function isPointsLayer(layer) {
  var features = layer.getSource().getFeatures();
  for (var i = 0; i < features.length; i++) {
    if (features[i].getGeometry().getType() === 'Point') {
      return true;
    }
  }
  return false; // The layer does not contain any point features
}

export function cloneStyleFromLayer(layer) {
  let style = layer?.getStyle();
  if (!(style instanceof Function)) {
    style = style?.clone();
  }
  return style;
}

export function getLayersNames(map) {
  const olLayers = map.getLayers().getArray();
  let layerNames = olLayers.map((x) => [x.get('name'), x.getZIndex()])?.filter((x) => !layerstoIgnore.includes(x[0]));
  layerNames.sort((a, b) => b[1] - a[1]);
  return layerNames;
}

export function getZIndexNext(map) {
  let zIndexes = [];
  const olLayers = map
    .getLayers()
    .getArray()
    .filter((layer) => !layerstoIgnore.includes(layer.get('name')));

  olLayers.forEach((layer) => {
    if (layer instanceof LayerGroup) {
      const subLayers = layer.getLayers().getArray();
      subLayers.forEach((subLayer) => {
        zIndexes.push(subLayer.getZIndex());
      });
    }
    zIndexes.push(layer.getZIndex());
  });

  // let zIndexes = getLayersNames(map)?.map(([_, zIndex]) => zIndex) ?? [];
  zIndexes = zIndexes.filter((index) => index);
  const nextZIndex = zIndexes.length > 0 ? Math.max(...zIndexes) + 1 : 1;
  return nextZIndex;
}

function getIndexForMarkersLayerName(map) {
  map.markerIndex ??= 1;
  let markerIndex = map.markerIndex++;
  return markerIndex;
}

//AM not used? we need to pass a parameter for deletion
// export function deleteMarkers(map) {
//   let layers = map
//     .getLayers()
//     .getArray()
//     .filter((layer) => layer.get('name') === 'markers');
//   layers.forEach((layer) => {
//     map.removeLayer(layer);
//   });
// }

function getGroupExtent(layerGroup) {
  var extent = createEmpty();
  layerGroup.getLayers().forEach(function (layer) {
    if (layer.getSource().getExtent() != undefined) {
      if (layer.getSource().getExtent()) {
        extend(extent, layer.getSource().getExtent());
      }
    }
  });
  return extent;
}

function getExtentFromMap(map) {
  var extent = createEmpty();
  var layers = map.getLayers().getArray();

  layers.forEach(function (layer) {
    var layerName = layer.get('name');
    if (!dictLayerNames.hasOwnProperty(layerName) && !layerstoIgnore.includes(layerName)) {
      if (layer instanceof VectorLayer) {
        if (layer.getSource().getExtent() != undefined) {
          extend(extent, layer.getSource().getExtent());
        }
      } else if (layer instanceof LayerGroup) {
        extend(extent, getGroupExtent(layer));
      }
    }
  });
  return extent;
}

function getExtentFromClusterSource(source) {
  const features = source.getFeatures();
  let extent = createEmpty();
  features.forEach(function (feature) {
    const clusteredFeatures = feature.get('features');
    clusteredFeatures.forEach(function (clusteredFeature) {
      extend(extent, clusteredFeature.getGeometry().getExtent());
    });
  });
  return extent;
}

function getExtentFromPoints(mapProjection, points) {
  // Initialize extent with initial values
  let minLon = Infinity;
  let minLat = Infinity;
  let maxLon = -Infinity;
  let maxLat = -Infinity;

  points.forEach((point) => {
    minLon = Math.min(minLon, point.long);
    minLat = Math.min(minLat, point.lat);
    maxLon = Math.max(maxLon, point.long);
    maxLat = Math.max(maxLat, point.lat);
  });

  const extent = [minLon, minLat, maxLon, maxLat];

  const transformedExtent = transformExtent(extent, 'EPSG:4326', mapProjection);

  return transformedExtent;
}

export const getExtentFromLayers = (layers) => {
  const defaultExtent = [-180, -90, 180, 90];
  const isValidExtent = (extent) => !extent.some((c) => c === Infinity || c === -Infinity);
  const addExtent = (layer) => {
    let source = layer.getSource();
    let sourceExtent = source instanceof Cluster ? source.getSource().getExtent() : source.getExtent();
    if (sourceExtent) {
      if (isValidExtent(sourceExtent)) {
        extend(extent, sourceExtent);
      }
    }
  };
  if (!Array.isArray(layers)) {
    layers = [layers];
  }
  let extent = createEmpty();
  layers.forEach((layer) => {
    if (layer instanceof LayerGroup) {
      let subLayers = layer.getLayers().getArray();
      subLayers.forEach((subLayer) => {
        addExtent(subLayer);
      });
    } else {
      addExtent(layer);
    }
  });
  return extent;
};

export async function getSourceLayerFromJSON({ layerName }) {
  let layer = getAvailablelayerByKey(true, true, layerName);

  return layer;
  // let features = await loadGeoJSONFeatures(source);

  //return features;
}

export async function getSourceFromJSON({ map, layerName, layerKey, isFromGeoServer }) {
  let features;
  if (!layerName && layerKey) {
    layerName = dictLayerNames[layerKey].label;
  }
  if (!layerKey && layerName) {
    layerKey = Object.keys(dictLayerNames).find((key) => dictLayerNames[key].label === layerName);
  }
  if (!isFromGeoServer) {
    let layer = map
      .getLayers()
      .getArray()
      .find((layer) => layer.get('name') == layerName);
    if (layer) {
      let source = layer.getSource();
      features = await loadGeoJSONFeatures(source);
    }
  } else {
    features = await getFeauresFromJson(layerKey);
  }

  return features;
}

async function loadFeaturesAsync(source) {
  console.log(source);
  return new Promise((resolve, reject) => {
    source.on('featuresloadend', (event) => {
      const features = source.getFeatures();
      if (features.length > 0) {
        resolve(features);
      } else {
        reject(new Error('No features loaded'));
      }
    });

    source.on('featuresloaderror', (event) => {
      reject(new Error('Error loading features'));
    });
  });
}

// Use the async function to load features and handle them
async function loadGeoJSONFeatures(vectorSource) {
  try {
    const features = await loadFeaturesAsync(vectorSource);
    //console.log('Loaded features asynchronously:', features);
    return features;
  } catch (error) {
    console.error('Error loading features:', error);
  }
}

export function fitExtentAndCenterMap(map, layers) {
  let extent = layers ? getExtentFromLayers(layers) : getExtentFromMap(map);

  const view = map.getView();
  // const resolution = view.getResolutionForExtent(extent, map.getSize());
  // const padding = getMapPadding(extent, resolution);

  if (extent) {
    let isEmpty = isEmptyExtent(extent);
    let center = getCenter(extent);
    view.setCenter(center);
    if (!isEmpty) {
      view.fit(extent, map.getSize());
      let zoom = Math.min(view.getZoom(), 8);
      view.setZoom(zoom - 1);
    } else {
      view.setZoom(7);
    }

    // view.fit(extent, { padding: padding });
    map.setView(view);
  }
}

export function _centerMapAndFitExtentByPoints({ map, points, zoom, padding }) {
  if (!map || !points?.length) return;
  if (!padding) {
    padding = 50;
  }

  let defaultZoom = 6;

  const view = map.getView();

  // ---- Single point ----
  if (points.length === 1) {
    const p = points[0];
    const coord = fromLonLat([p.long, p.lat]);

    view.setCenter(coord);
    view.setZoom(zoom ?? defaultZoom);
    view.animate({ duration: 400 });

    return;
  }

  // ---- Multiple points ----
  const coords3857 = points.map((p) => fromLonLat([p.long, p.lat]));
  const extent = olExtent.boundingExtent(coords3857);

  view.fit(extent, {
    padding: Array(4).fill(padding),
    duration: 500,
    maxZoom: zoom ?? defaultZoom,
  });
}
function isEmptyExtent(extent) {
  return extent[2] <= extent[0] || extent[3] <= extent[1];
}

function getMapPadding(extent, resolution) {
  const minPadding = 200 * 1000; // Minimum padding in m

  let [paddingX, paddingY] = [
    Math.max(getWidth(extent) * 0.5, minPadding),
    Math.max(getHeight(extent) * 0.5, minPadding),
  ];
  const padding = [paddingY, paddingX, paddingY, paddingX].map((x) => x / resolution);
  return padding;
}

export function addLayerPoint({
  point,
  layerName,
  style,
  map,
  isForMarkers,
  hasDeleteBtn,
  hasLabels,
  legend,
  events,
  id,
  noTooltip,
}) {
  style = style ? getolStyle(style, hasLabels) : cloneStyle(styleStar);

  const pt = fromLonLat([point.long, point.lat]);
  const pointFeature = new Feature({
    geometry: new Point(pt),
    label: hasLabels ? point.label : '',
    id: id,
  });

  const zIndex = getZIndexNext(map);

  const vectorSource = new VectorSource({
    features: [pointFeature],
  });

  if (isForMarkers) {
    layerName = layerName + ' ' + getIndexForMarkersLayerName(map);
  }

  const layer = new VectorLayer({
    name: layerName,
    visible: true,
    zIndex: zIndex,
    source: vectorSource,
    style: style,
    styleOrigin: cloneStyle(style),
    hasDeleteBtn: hasDeleteBtn,
    hasLabels: hasLabels,
    legend: legend,
  });

  if (events?.hover) {
    addInteractionHover({ map, layer, events, noTooltip });
  }

  map.addLayer(layer);
  return layer;
}

export function addLayerPoints({
  points,
  layerName,
  style,
  map,
  hasInfo,
  isForMarkers,
  hasDeleteBtn,
  hasLabels,
  legend,
  events,
  noTooltip,
}) {
  //console.log('hasLabels' + hasLabels); // should print "true"
  style = style ? getolStyle(style, hasLabels) : cloneStyle(styleStar);

  let features = [];
  points.forEach((point) => {
    const pt = fromLonLat([point.long, point.lat]);
    const pointFeature = new Feature({
      geometry: new Point(pt),
      label: hasLabels ? point.label : '',
      info: point.info,
      id: point.id,
    });
    features.push(pointFeature);
  });

  const zIndex = getZIndexNext(map);

  const vectorSource = new VectorSource({
    features: features,
  });

  if (isForMarkers) {
    layerName = layerName + ' ' + getIndexForMarkersLayerName(map);
  }

  const layer = new VectorLayer({
    name: layerName,
    visible: true,
    zIndex: zIndex,
    source: vectorSource,
    style: style,
    styleOrigin: cloneStyle(style),
    hasDeleteBtn: hasDeleteBtn,
    hasLabels: hasLabels,
    legend: legend,
  });

  if (hasInfo) {
    addInteractionHover({ map, layer });
  }

  if (events?.hover) {
    addInteractionHover({ map, layer, events, noTooltip });
  }

  map.addLayer(layer);
  return layer;
}

export function addJsonLayer({ map, geoJsonString, layerName, style, hasDeleteBtn }) {
  var style = getStyleForJsonLayer(style);

  const zIndex = getZIndexNext(map);

  const vectorLayer = new VectorLayer({
    name: layerName,
    visible: true,
    zIndex: zIndex,
    source: new VectorSource({
      url: 'data:,' + encodeURIComponent(geoJsonString),
      format: new GeoJSON(),
    }),
    style: style,
    styleOrigin: cloneStyle(style),
    hasDeleteBtn: hasDeleteBtn,
    hasLabels: false,
  });

  map.addLayer(vectorLayer);
  return vectorLayer;
}

export function addLayerLine({ points, layerName, map, style, hasDeleteBtn, legend }) {
  style = style ? getolStyle(style) : cloneStyle(styleShadowDistance);

  var ring = [];
  points.forEach((point) => {
    const pt = fromLonLat([point.long, point.lat]); //fromLonLat from (EPSG:4326)  to (EPSG:3857)
    ring.push(pt);
  });

  var lineString = new LineString(ring);
  var lineFeature = new Feature({ geometry: lineString });
  var vectorSource = new VectorSource({
    features: [lineFeature],
  });

  const zIndex = getZIndexNext(map);

  var vectorLayer = new VectorLayer({
    source: vectorSource,
    name: layerName,
    visible: true,
    zIndex: zIndex,
    style: style,
    styleOrigin: cloneStyle(style),
    hasDeleteBtn: hasDeleteBtn,
    legend: legend,
  });
  map.addLayer(vectorLayer);

  return vectorLayer;
}

export function addLayerContour({ points, layerName, map, style, hasDeleteBtn, legend }) {
  style = style ? getolStyle(style) : cloneStyle(styleShadowDistance);

  var ring = [];
  points.forEach((point) => {
    const pt = fromLonLat([point.long, point.lat]); //fromLonLat from (EPSG:4326)  to (EPSG:3857)
    ring.push(pt);
  });
  ring.push(fromLonLat([points[0].long, points[0].lat])); //close the ring

  var polygon = new Polygon([ring]); //  simple case: one ring only.
  var feature = new Feature(polygon);
  var vectorSource = new VectorSource();
  vectorSource.addFeature(feature);

  const zIndex = getZIndexNext(map);

  var vectorLayer = new VectorLayer({
    source: vectorSource,
    name: layerName,
    visible: true,
    zIndex: zIndex,
    style: style,
    styleOrigin: cloneStyle(style),
    hasDeleteBtn: hasDeleteBtn,
    legend: legend,
  });
  map.addLayer(vectorLayer);
  return vectorLayer;
}

export function addLayerCircle({ center, radius, layerName, map, style, hasDeleteBtn, noVertices }) {
  style = style ? getolStyle(style) : cloneStyle(styleShadowDistance);

  //const centerPoint = fromLonLat([center.long, center.lat]);

  const center4326 = [center.long, center.lat];

  const circlePolygon = circular(
    center4326,
    radius * 1000, // meters
    noVertices || 128, // number of vertices
  );

  circlePolygon.transform('EPSG:4326', map.getView().getProjection());

  const circleFeature = new Feature(circlePolygon);

  circleFeature.setStyle(style);

  var vectorSource = new VectorSource();
  vectorSource.addFeature(circleFeature);

  const zIndex = getZIndexNext(map);

  const vectorLayer = new VectorLayer({
    source: vectorSource,
    name: layerName,
    visible: true,
    zIndex: zIndex,
    style: style,
    styleOrigin: cloneStyle(style),
    hasDeleteBtn: hasDeleteBtn,
  });

  map.addLayer(vectorLayer);
  return vectorLayer;
}

export function addLayerPolygoneAndPoints({
  points,
  map,
  layerName,
  styles,
  unit,
  hasDeleteBtn,
  hasLabels,
  events,
  legend,
}) {
  const zIndex = getZIndexNext(map);

  let stylePoints = styles?.points ? getolStyle(styles?.points, hasLabels) : cloneStyle(styleStar);
  let styleContour = styles?.contour ? getolStyle(styles?.contour) : cloneStyle(stylePolygon);

  let ring = [];
  let features = [];
  points.forEach((point) => {
    const pt = fromLonLat([point.long, point.lat]);
    const pointFeature = new Feature({
      geometry: new Point(pt),
      label: hasLabels ? point.label : '',
      id: point.id ? point.id : '',
      info: point.info + ' ' + unit,
    });
    features.push(pointFeature);
    ring.push(pt);
  });
  ring.push(fromLonLat([points[0].long, points[0].lat])); //close the ring

  const vectorSourcePoints = new VectorSource({
    features: features,
  });
  const vectorLayerpoints = new VectorLayer({
    name: layerName + '(Points)',
    visible: true,
    zIndex: zIndex + 2,
    source: vectorSourcePoints,
    style: stylePoints,
    styleOrigin: cloneStyle(stylePoints),
  });

  addInteractionHover({ map, layer: vectorLayerpoints, events });

  var vectorSourcePolygon = new VectorSource();
  vectorSourcePolygon.addFeature(new Feature(new Polygon([ring])));
  var vectorLayerPolygon = new VectorLayer({
    source: vectorSourcePolygon,
    name: layerName + '(Contour)',
    visible: true,
    zIndex: zIndex + 1,
    style: styleContour,
    styleOrigin: cloneStyle(styleContour),
  });

  let layer = new LayerGroup({
    name: layerName,
    visible: true,
    zIndex: zIndex,
    layers: [vectorLayerpoints, vectorLayerPolygon],
    hasDeleteBtn: hasDeleteBtn,
    hasLabels: hasLabels,
    legend: legend,
  });

  map.addLayer(layer);
  return layer;
}

export function addLayerRadialLines({
  centerPoint,
  endPoints = [],
  layerName,
  map,
  styles,
  hasDeleteBtn,
  hasLabels,
  legend,
  threshold,
  unit,
  events,
  noTooltip,
  lineValueKey = 'pfd',
  lineInfo,
  showCenter = true,
  showEndPoints = true,
  showLines = true,
  lineTooltip = true,
}) {
  const zIndex = getZIndexNext(map);
  const layers = [];

  const getPointCoord = (point) => {
    if (!point) {
      return null;
    }
    if (Array.isArray(point)) {
      return point;
    }
    if (Array.isArray(point.coord)) {
      return point.coord;
    }
    if (Array.isArray(point.coords)) {
      return point.coords;
    }
    const lon = point.long ?? point.lon ?? point.lng ?? point.longitude;
    const lat = point.lat ?? point.latitude;
    if (lon === undefined || lat === undefined) {
      return null;
    }
    return [lon, lat];
  };

  const resolveStyle = (style, labels) => {
    if (!style) {
      return null;
    }
    if (style instanceof Function) {
      return style;
    }
    return getolStyle(style, labels);
  };

  const resolveLineStyle = (style) => {
    if (!style) {
      return null;
    }
    if (style instanceof Function) {
      return style;
    }
    if (typeof style === 'string') {
      return getolStyle(style);
    }
    if (
      style.line ||
      style.contour ||
      style.point ||
      style.triangle ||
      style.square ||
      style.star ||
      style.cross ||
      style.diamond
    ) {
      return getolStyle(style);
    }
    if (style.stroke) {
      return getolStyle({ line: style });
    }
    return getolStyle(style);
  };

  const resolveStyleValue = (style, feature) => {
    return style instanceof Function ? style(feature) : style;
  };

  const centerStyle =
    resolveStyle(styles?.center ?? styles?.point ?? styles?.points, hasLabels) ?? cloneStyle(styleStar);
  const endPointStyle =
    resolveStyle(styles?.endPoint ?? styles?.points ?? styles?.point, hasLabels) ?? cloneStyle(styleStar);

  const baseLineStyle = resolveLineStyle(styles?.line ?? styles?.lines) ?? getolStyle('line blue 2');

  const thresholdConfig = threshold && typeof threshold === 'object' ? threshold : null;
  const thresholdValue = thresholdConfig?.value ?? threshold;
  const valueKey = thresholdConfig?.property ?? lineValueKey;
  const thresholdBelowStyle = resolveLineStyle(thresholdConfig?.belowStyle ?? styles?.lineBelow);
  const thresholdAboveStyle = resolveLineStyle(thresholdConfig?.aboveStyle ?? styles?.lineAbove);

  let baseLineWidth;
  let defaultBelowStyle;
  let defaultAboveStyle;

  const getBaseLineWidth = (feature) => {
    if (baseLineWidth) {
      return baseLineWidth;
    }
    const resolvedBase = resolveStyleValue(baseLineStyle, feature);
    const stroke = resolvedBase?.getStroke?.();
    baseLineWidth = stroke?.getWidth?.() ?? 2;
    return baseLineWidth;
  };

  const getDefaultBelowStyle = (feature) => {
    if (!defaultBelowStyle) {
      defaultBelowStyle = getolStyle({
        line: {
          stroke: {
            color: 'green',
            width: getBaseLineWidth(feature),
            lineDash: [2, 5],
          },
        },
      });
    }
    return defaultBelowStyle;
  };

  const getDefaultAboveStyle = (feature) => {
    if (!defaultAboveStyle) {
      defaultAboveStyle = getolStyle({
        line: {
          stroke: {
            color: 'red',
            width: getBaseLineWidth(feature),
          },
        },
      });
    }
    return defaultAboveStyle;
  };

  const buildLineInfo = (endPoint, value) => {
    if (lineInfo instanceof Function) {
      return lineInfo(endPoint, value);
    }
    if (typeof lineInfo === 'string') {
      return endPoint?.[lineInfo] ?? '';
    }
    if (endPoint?.info) {
      return endPoint.info;
    }
    if (value === undefined || value === null) {
      return '';
    }
    const resolvedUnit = unit ?? (valueKey === 'pfd' ? 'dB(W/(m²·MHz))' : null);
    if (resolvedUnit) {
      return `${valueKey}: ${value} ${resolvedUnit}`;
    }
    return `${valueKey}: ${value}`;
  };

  const centerCoord = getPointCoord(centerPoint);
  const centerMapCoord = centerCoord ? fromLonLat(centerCoord) : null;

  if (showCenter && centerMapCoord) {
    const centerFeature = new Feature({
      geometry: new Point(centerMapCoord),
      label: hasLabels ? centerPoint?.label ?? '' : '',
      info: centerPoint?.info,
      id: centerPoint?.id ?? '',
    });

    const centerSource = new VectorSource({ features: [centerFeature] });
    const centerLayer = new VectorLayer({
      name: layerName + '(Center)',
      visible: true,
      zIndex: zIndex + 2,
      source: centerSource,
      style: centerStyle,
      styleOrigin: cloneStyle(centerStyle),
      hasLabels: hasLabels,
    });

    if (events?.hover) {
      addInteractionHover({ map, layer: centerLayer, events, noTooltip });
    }

    layers.push(centerLayer);
  }

  let endPointFeatures = [];
  if (showEndPoints && endPoints.length > 0) {
    endPoints.forEach((endPoint) => {
      const endPointCoord = getPointCoord(endPoint);
      if (!endPointCoord) {
        return;
      }
      const endPointFeature = new Feature({
        geometry: new Point(fromLonLat(endPointCoord)),
        label: hasLabels ? endPoint.label ?? '' : '',
        info: endPoint.info,
        id: endPoint.id ?? '',
      });
      endPointFeatures.push(endPointFeature);
    });

    if (endPointFeatures.length > 0) {
      const endPointSource = new VectorSource({ features: endPointFeatures });
      const endPointLayer = new VectorLayer({
        name: layerName + '(EndPoints)',
        visible: true,
        zIndex: zIndex + 2,
        source: endPointSource,
        style: endPointStyle,
        styleOrigin: cloneStyle(endPointStyle),
        hasLabels: hasLabels,
      });

      if (events?.hover) {
        addInteractionHover({ map, layer: endPointLayer, events, noTooltip });
      }

      layers.push(endPointLayer);
    }
  }

  let lineFeatures = [];
  if (showLines && endPoints.length > 0) {
    endPoints.forEach((endPoint) => {
      const endPointCoord = getPointCoord(endPoint);
      if (!endPointCoord) {
        return;
      }
      const startCoord = getPointCoord(endPoint?.start ?? endPoint?.center);
      const startMapCoord = startCoord ? fromLonLat(startCoord) : centerMapCoord;
      if (!startMapCoord) {
        return;
      }
      const lineString = new LineString([startMapCoord, fromLonLat(endPointCoord)]);
      const value = endPoint?.[valueKey];
      const lineFeature = new Feature({
        geometry: lineString,
        info: buildLineInfo(endPoint, value),
        id: endPoint?.id ?? '',
        label: endPoint?.label ?? '',
      });
      if (value !== undefined) {
        lineFeature.set(valueKey, value);
      }
      lineFeatures.push(lineFeature);
    });
  }

  if (lineFeatures.length > 0) {
    const lineStyleFunction = (feature) => {
      if (thresholdValue !== undefined && thresholdValue !== null) {
        const value = feature.get(valueKey);
        if (value !== undefined && value !== null) {
          if (value <= thresholdValue) {
            return resolveStyleValue(thresholdBelowStyle, feature) ?? getDefaultBelowStyle(feature);
          }
          return resolveStyleValue(thresholdAboveStyle, feature) ?? getDefaultAboveStyle(feature);
        }
      }
      return resolveStyleValue(baseLineStyle, feature);
    };

    const lineSource = new VectorSource({ features: lineFeatures });
    const lineLayer = new VectorLayer({
      name: layerName + '(Lines)',
      visible: true,
      zIndex: zIndex + 1,
      source: lineSource,
      style: lineStyleFunction,
      styleOrigin: baseLineStyle,
      hasDeleteBtn: hasDeleteBtn,
      legend: legend,
    });

    if (lineTooltip) {
      const lineTooltipHandles = addInteractionLineTooltip({
        map,
        layer: lineLayer,
      });
      if (lineTooltipHandles?.interaction) {
        lineLayer.set(lineTooltipInteractionKey, lineTooltipHandles.interaction);
      }
      if (lineTooltipHandles?.overlay) {
        lineLayer.set(lineTooltipOverlayKey, lineTooltipHandles.overlay);
      }
    }

    layers.push(lineLayer);
  }

  if (layers.length === 0) {
    return null;
  }

  const layerGroup = new LayerGroup({
    name: layerName,
    visible: true,
    zIndex: zIndex,
    layers: layers,
    hasDeleteBtn: hasDeleteBtn,
    hasLabels: hasLabels,
    legend: legend,
  });

  map.addLayer(layerGroup);
  return layerGroup;
}

function updateStyles(styles, stls, styleKey) {
  for (const [key, value] of Object.entries(stls)) {
    if (value) {
      styles[styleKey] = { [key]: value };
      return;
    }
  }
}

function getFormattedStyles(styles) {
  let _styles = {};
  let { all, groups, selected } = styles;
  if (all) {
    let stls = getStyleFromString(all);
    if (stls) {
      updateStyles(_styles, stls, 'all');
    }
  }
  if (groups) {
    Object.entries(groups).forEach((entry) => {
      let [gp, styl] = entry;
      let stls = getStyleFromString(styl);
      if (stls) {
        updateStyles(_styles, stls, gp);
      }
    });
  }
  if (selected) {
    let stls = getStyleFromString(selected);
    if (stls) {
      updateStyles(_styles, stls, 'selected');
    }
  }
  return _styles;
}

export function addLayerCluster({ points, layerName, map, styles, hasDeleteBtn, legend, events }) {
  const styleCache = {};
  let _styles = {};
  let myStyle;

  if (styles) {
    _styles = getFormattedStyles(styles);
  }

  let _style = cloneStyle(styleCluster);
  myStyle = (feature) => {
    return _style(feature, styleCache, _styles);
  };

  const zIndex = getZIndexNext(map);

  const features = new Array(points.length);
  for (let i = 0; i < points.length; ++i) {
    const pt = fromLonLat([points[i].long, points[i].lat]);
    features[i] = new Feature(new Point(pt));
    features[i].set('group', points[i].group);
    features[i].set('label', points[i].label);
    features[i].set('props', points[i].props);
  }

  const source = new VectorSource({
    features: features,
    strategy: bboxStrategy, // Use bounding box strategy
  });

  const clusterSource = new Cluster({
    distance: 40,
    source: source,
  });
  //let extent = getExtentFromPoints(map.getView().getProjection(), points);
  const bufferedExtent = olExtent.buffer(map.getView().calculateExtent(map.getSize()), 100); // Add a 100px buffer

  const layerClusters = new VectorLayer({
    source: clusterSource,
    name: layerName,
    visible: true,
    zIndex: zIndex,
    style: myStyle,
    styleOrigin: cloneStyle(myStyle),
    hasDeleteBtn: hasDeleteBtn,
    legend: legend,
    extent: bufferedExtent,
  });

  map.addLayer(layerClusters);

  let popup = addInteractionPopup({ map, layer: layerClusters, events });

  return { layerClusters, popup };
}

export function createMarkerPickerTool(map, groupDD, groupDMS) {
  let draw;
  if (!window[map.getTarget()]?.positionPicker) {
    draw = new Draw({
      type: 'Point',
    });
    map.addInteraction(draw);
  } else {
    draw = window[map.getTarget()]?.positionPicker;
  }
  draw.setActive(false);
  draw.on('drawend', function (evt) {
    const feature = evt.feature;
    const coordinates = feature.getGeometry().getCoordinates();
    const dd = toLonLat(coordinates);
    var sDMS = toStringHDMS(dd);
    const dms = parseDMS(sDMS);
    groupDD.long.value = dd[0];
    groupDD.lat.value = dd[1];

    groupDMS.long.longD.value = dms.long.d;
    groupDMS.long.longM.value = dms.long.m;
    groupDMS.long.longS.value = dms.long.s;
    groupDMS.long.select.value = dms.long.dir;

    groupDMS.lat.latD.value = dms.lat.d;
    groupDMS.lat.latM.value = dms.lat.m;
    groupDMS.lat.latS.value = dms.lat.s;
    groupDMS.lat.select.value = dms.lat.dir;
  });

  return draw;
}

export function createMarkerPickerToolExternal(map, callabak) {
  const vectorSource = new VectorSource();
  const vectorLayer = new VectorLayer({
    name: 'getCoordinates',
    visible: true,
    zIndex: 9999,
    source: vectorSource,
    style: pointCoordsStyle,
  });

  let draw = new Draw({
    type: 'Point',
    source: vectorSource,
  });
  map.addInteraction(draw);
  map.addLayer(vectorLayer);
  draw.setActive(false);
  draw.on('drawend', function (evt) {
    const feature = evt.feature;
    vectorSource.clear();
    // const point = new Feature({
    //   geometry: new Point(coordinates),
    // });
    // vectorSource.addFeature(feature);
    const coordinates = feature.getGeometry().getCoordinates();
    const dd = toLonLat(coordinates);
    var sDMS = toStringHDMS(dd);
    const dms = parseDMS(sDMS);

    const result = {
      dd: {
        long: dd[0],
        lat: dd[1],
      },
      dms: {
        long: {
          d: dms.long.d,
          m: dms.long.m,
          s: dms.long.s,
          dir: dms.long.dir,
        },
        lat: {
          d: dms.lat.d,
          m: dms.lat.m,
          s: dms.lat.s,
          dir: dms.lat.dir,
        },
      },
    };
    callabak(result);
  });
  return {
    draw,
    source: vectorSource,
  };
}

export function createGetContourToolExternal(map, callabak) {
  const vectorSource = new VectorSource();
  const vectorLayer = new VectorLayer({
    name: 'getContour',
    visible: true,
    zIndex: 9999,
    source: vectorSource,
    style: styleDraw,
  });

  let draw = new Draw({
    type: 'Polygon',
    source: vectorSource,
  });
  map.addInteraction(draw);
  map.addLayer(vectorLayer);
  draw.setActive(false);

  // Create a Snap interaction to help with vertex snapping
  var snap = new Snap({
    source: vectorLayer.getSource(),
  });
  map.addInteraction(snap);

  draw.on('drawstart', function (evt) {
    vectorSource.clear();
  });
  draw.on('drawend', function (evt) {
    let contour = [];
    const feature = evt.feature;
    vectorSource.clear();
    // vectorSource.addFeature(feature);
    const coordinates = feature.getGeometry().getCoordinates();
    if (coordinates.length > 0) {
      coordinates[0].forEach((c) => {
        contour.push(toLonLat(c));
      });
    }
    callabak(contour);
  });
  return {
    draw,
    source: vectorSource,
  };
}

export function getVectorLayersAtPixel(map, evt) {
  let layersAtPixel = [];
  map.forEachFeatureAtPixel(evt.pixel, function (feature, layer) {
    if (layer) {
      layersAtPixel.push(layer);
    }
  });
  return layersAtPixel;
}
export function getVectorLayerNamesAtPixel(map, evt) {
  let layerNames = [];
  map.forEachFeatureAtPixel(evt.pixel, function (feature, layer) {
    if (layer && layer.get('name')) {
      layerNames.push(layer.get('name'));
    }
  });
  return layerNames;
}

export function getUrlForInfoFromImageOrTile(map, evt, idwm) {
  let layerName = idwm ? 'idwm' : 'unmap';
  let url;
  let layer = map
    .getLayers()
    .getArray()
    .find(
      (layer) =>
        !(layer instanceof VectorLayer) &&
        !(layer instanceof LayerGroup) &&
        layer.get('name')?.toLowerCase()?.includes(layerName),
    );

  if (layer) {
    let source = layer.getSource();
    let view = map.getView();
    let viewResolution = view.getResolution();

    let coordinates = evt.coordinate;
    let viewProjection = view.getProjection();
    //if (idwm) {
    viewProjection = projectionForIdwmGetInfo;
    coordinates = transform(evt.coordinate, 'EPSG:3857', 'EPSG:4326');
    //}
    url = source.getFeatureInfoUrl(coordinates, 0.0001, viewProjection, {
      INFO_FORMAT: 'text/html',
      FEATURE_COUNT: 50,
    });

    url += '&embedded=true';
  }
  return url;
}

export function _displayMapZoomLevel(map) {
  let zoom = map.getView().getZoom();
  console.log(zoom);
  return;
}

export function _displayMapZIndexes(map) {
  let result = [];
  let layers = map.getLayers().getArray();
  layers.forEach((layer) => {
    if (layer instanceof LayerGroup) {
      let subLayers = layer.getLayers().getArray();
      let subresult = [];
      subLayers.forEach((subLayer) => {
        subresult.push(getLayerInfo(subLayer));
      });
      subresult.sort((a, b) => {
        return b.zIndex - a.zIndex;
      });
      let info1 = getLayerInfo(layer);
      info1['subLayers'] = subresult;
      result.push(info1);
    } else {
      result.push(getLayerInfo(layer));
    }
  });

  result.sort((a, b) => {
    return b.zIndex - a.zIndex;
  });

  console.log(result);

  function getLayerInfo(layer) {
    return {
      name: layer.get('name'),
      zIndex: layer.getZIndex(),
    };
  }

  return;
}

function cleanupLineTooltip(map, layer) {
  if (!map || !layer) {
    return;
  }
  if (layer instanceof LayerGroup) {
    const subLayers = layer.getLayers().getArray();
    subLayers.forEach((subLayer) => {
      cleanupLineTooltip(map, subLayer);
    });
  }
  const interaction = layer.get(lineTooltipInteractionKey);
  if (interaction) {
    map.removeInteraction(interaction);
  }
  const overlay = layer.get(lineTooltipOverlayKey);
  if (overlay) {
    map.removeOverlay(overlay);
  }
}

export function removeLayersByName(map, layers) {
  if (!layers) {
    return;
  }
  if (!Array.isArray(layers)) {
    layers = layers.split(',');
  }
  let layersToRemove = map
    .getLayers()
    .getArray()
    .filter((layer) => layers.includes(layer.get('name')));
  layersToRemove.forEach((layer) => {
    cleanupLineTooltip(map, layer);
    map.removeLayer(layer);
  });
}

export function getLayerObjectByName(map, layerName) {
  if (!layerName) {
    return;
  }

  let layer = map
    .getLayers()
    .getArray()
    .find((layer) => layer.get('name') == layerName);

  return layer;
}

export function getLayerObjectByKey(map, layerKey) {
  if (!layerKey) {
    return;
  }

  let layerName = dictLayerNames[layerKey].label;
  if (layerName) {
    let layer = getLayerObjectByName(map, layerName);
    return layer;
  }
}

export function removeLayersByPrefix(map, layersPrefixes) {
  if (!layersPrefixes) {
    return;
  }

  // Ensure layersPrefixes is an array
  if (!Array.isArray(layersPrefixes)) {
    layersPrefixes = layersPrefixes.split(',');
  }

  // Retrieve all layers from the map
  const allLayers = map.getLayers().getArray();
  const layersToRemove = [];

  // Filter layers that have names starting with any of the provided prefixes
  allLayers.forEach((layer) => {
    const layerName = layer.get('name');
    if (layerName) {
      layersPrefixes.forEach((prefix) => {
        if (layerName.startsWith(prefix)) {
          layersToRemove.push(layer);
        }
      });
    }
  });

  layersToRemove.forEach((layer) => {
    cleanupLineTooltip(map, layer);
    map.removeLayer(layer);
  });
}

export function addOverviewMapControl(map, layerKey, isGeoServerAvailable) {
  if (layerKey == 'idwmGroup' && !isGeoServerAvailable) {
    layerKey = 'idwmGroupWebServer';
  }
  let layer = getLayerObjectByKey(map, layerKey);
  if (layer) {
    const overviewMapControl = new OverviewMap({
      layers: [layer],
      collapsed: false, // Start the overview map open by default
      className: 'ol-overviewmap oim-ol-overviewmap',
      collapseLabel: '\u00BB',
      label: '\u00AB',
    });
    map.addControl(overviewMapControl);
  }
}

export function _getAllClusterSources(map) {
  const clusterSources = [];

  function checkLayer(layer) {
    if (layer instanceof VectorLayer) {
      const source = layer.getSource();
      if (source instanceof Cluster) {
        clusterSources.push(source);
      }
    } else if (layer instanceof LayerGroup) {
      layer.getLayers().forEach((subLayer) => checkLayer(subLayer));
    }
  }

  map.getLayers().forEach((layer) => checkLayer(layer));

  return clusterSources;
}

export function _getAllLayers(map) {
  const allLayers = map.getLayers().getArray();
  return allLayers;
}
